import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ClickAwayListener, Grow, Popper } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import dayjs from 'dayjs'
import SelectLabel from '../../../DenaliAnalysis/DenaliScopePicker/SelectLabel'
import SydButton from '../../../../commonDesign/Button'
import SydInput from '../../../../commonDesign/SydInput'
import SydLabel from '../../../../commonDesign/SydLabel'
import { KEYS } from '../../../../../constants'
import {
  DATE_RANGE_PRESETS,
  EMPTY_DATE_RANGE_DRAFT,
  dateRangePickerValuesEqual,
  isCustomDateRangeValid,
  resolveDateRangePickerValues
} from './queryBuilders'
import { useUserLoginActivityContext } from './UserLoginActivityProvider'

const PRESET_OPTIONS = [
  { value: DATE_RANGE_PRESETS.QTD, label: 'QTD', summaryLabel: 'Quarter to date' },
  { value: DATE_RANGE_PRESETS.YTD, label: 'YTD', summaryLabel: 'Year to date' },
  { value: DATE_RANGE_PRESETS.L12M, label: 'L12M', summaryLabel: 'Last 12 months' },
  { value: DATE_RANGE_PRESETS.CUSTOM, label: 'Custom', summaryLabel: 'Custom' }
]

const TRIGGER_STYLE = {
  minWidth: 200,
  padding: '6px 10px',
  border: 'var(--border-neutral-thin)'
}

const PANEL_STYLE = { width: 300 }

const DATE_INPUT_PROPS = { style: { width: '100%' } }

const useStyles = makeStyles(() => ({
  presetGroup: {
    borderColor: 'var(--color-neutral-200)'
  },
  presetButton: {
    display: 'block',
    width: '100%',
    padding: '6px 4px',
    border: 'none',
    borderRight: '1px solid var(--color-neutral-200)',
    background: 'transparent',
    fontFamily: 'inherit',
    fontSize: 'var(--font-size-sm)',
    lineHeight: 1.25,
    textAlign: 'center',
    color: 'inherit',
    cursor: 'pointer',
    boxShadow: 'inset 0 0 0 1px transparent',
    '&.__selected': {
      fontWeight: 600,
      background: 'var(--color-neutral-200)',
      boxShadow: 'inset 0 0 0 1px var(--color-neutral-400)'
    },
    '&.__last': {
      borderRight: 'none'
    }
  },
  footer: {
    borderTop: '1px solid var(--color-neutral-200)',
    background: 'var(--color-neutral-100)'
  }
}))

function formatPickerDate (value) {
  if (!value) return ''

  const parsed = dayjs(value)

  return parsed.isValid() ? parsed.format('MMM D, YYYY') : value
}

function buildSummaryLabel (range) {
  const preset = range?.preset ?? null

  if (!preset) {
    return 'All dates'
  }

  const option = PRESET_OPTIONS.find((o) => o.value === preset)

  if (preset !== DATE_RANGE_PRESETS.CUSTOM) {
    return option?.summaryLabel ?? 'All dates'
  }

  const { startDate, endDate } = resolveDateRangePickerValues(range)
  const start = startDate?.trim() ?? ''
  const end = endDate?.trim() ?? ''

  if (!start && !end) return 'Custom'
  if (start && end) return `${formatPickerDate(start)} – ${formatPickerDate(end)}`
  if (start) return `From ${formatPickerDate(start)}`

  return `Through ${formatPickerDate(end)}`
}

function toCommittedDraft (dateRange) {
  return {
    preset: dateRange?.preset ?? null,
    startDate: dateRange?.startDate ?? '',
    endDate: dateRange?.endDate ?? ''
  }
}

export default function DateRangeFilter () {
  const classes = useStyles()
  const { dateRange, commitDateRange } = useUserLoginActivityContext()

  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(EMPTY_DATE_RANGE_DRAFT)

  const committed = useMemo(() => toCommittedDraft(dateRange), [dateRange])

  const summaryLabel = useMemo(
    () => buildSummaryLabel(committed),
    [committed]
  )

  const isDirty = useMemo(() => {
    if (!open) {
      return false
    }

    return !dateRangePickerValuesEqual(draft, committed)
  }, [open, draft, committed])

  const isCustomSelected = draft.preset === DATE_RANGE_PRESETS.CUSTOM
  const isDraftDefault = !draft.preset

  const showEndBeforeStartError = useMemo(() => {
    if (!isCustomSelected) {
      return false
    }

    const start = draft.startDate?.trim() ?? ''
    const end = draft.endDate?.trim() ?? ''

    if (!start || !end) {
      return false
    }

    const parsedStart = dayjs(start)
    const parsedEnd = dayjs(end)

    if (!parsedStart.isValid() || !parsedEnd.isValid()) {
      return false
    }

    return parsedEnd.isBefore(parsedStart, 'day')
  }, [isCustomSelected, draft.startDate, draft.endDate])

  const rangeValid = isCustomDateRangeValid(draft) && !showEndBeforeStartError

  const closeWithoutCommit = useCallback(() => {
    setOpen(false)
  }, [])

  const openPopover = useCallback(() => {
    setDraft({ ...committed })
    setOpen(true)
  }, [committed])

  const handleToggleOpen = useCallback(() => {
    if (open) {
      closeWithoutCommit()
      return
    }

    openPopover()
  }, [open, closeWithoutCommit, openPopover])

  const handleApply = useCallback((e) => {
    e?.stopPropagation?.()

    if (!rangeValid) {
      return
    }

    if (!isDirty) {
      closeWithoutCommit()
      return
    }

    const resolved = resolveDateRangePickerValues(draft)

    commitDateRange({
      preset: draft.preset,
      startDate: resolved.startDate,
      endDate: resolved.endDate
    })

    closeWithoutCommit()
  }, [rangeValid, isDirty, draft, commitDateRange, closeWithoutCommit])

  const handleResetDraft = useCallback((e) => {
    e?.stopPropagation?.()
    setDraft({ ...EMPTY_DATE_RANGE_DRAFT })
  }, [])

  const handlePresetSelect = useCallback((preset) => {
    if (preset === DATE_RANGE_PRESETS.CUSTOM) {
      setDraft((prev) => ({
        preset: DATE_RANGE_PRESETS.CUSTOM,
        startDate: prev.preset === DATE_RANGE_PRESETS.CUSTOM ? prev.startDate : '',
        endDate: prev.preset === DATE_RANGE_PRESETS.CUSTOM ? prev.endDate : ''
      }))

      return
    }

    setDraft({
      preset,
      startDate: '',
      endDate: ''
    })
  }, [])

  const handleStartChange = useCallback((e) => {
    setDraft((prev) => ({ ...prev, startDate: e?.target?.value ?? '' }))
  }, [])

  const handleEndChange = useCallback((e) => {
    setDraft((prev) => ({ ...prev, endDate: e?.target?.value ?? '' }))
  }, [])

  useEffect(() => {
    if (!open) return undefined

    const handler = (e) => {
      if (e.which === KEYS.ESC) {
        closeWithoutCommit()
        e.stopPropagation()
        e.preventDefault()
      }
    }

    window.addEventListener('keydown', handler, false)

    return () => window.removeEventListener('keydown', handler, false)
  }, [open, closeWithoutCommit])

  const renderMenu = useCallback((params) => {
    const { TransitionProps } = params ?? {}

    return (
      <Grow {...TransitionProps} style={{ transformOrigin: 'top' }}>
        <div className='opal-drop-menu below select-none' style={PANEL_STYLE}>
          <ClickAwayListener onClickAway={closeWithoutCommit}>
            <div>
              <div className='p-2 w-full'>
                <div
                  className={clsx('grid grid-cols-4 w-full border-1 radius-sm mb-2', classes.presetGroup)}
                  role='group'
                  aria-label='Date range preset'
                >
                  {PRESET_OPTIONS.map((opt, index) => {
                    const isLast = index === PRESET_OPTIONS.length - 1
                    const selected = draft.preset === opt.value

                    return (
                      <button
                        key={opt.value}
                        type='button'
                        aria-pressed={selected}
                        className={clsx(classes.presetButton, {
                          __selected: selected,
                          __last: isLast
                        })}
                        onClick={() => handlePresetSelect(opt.value)}
                      >
                        {opt.label}
                      </button>
                    )
                  })}
                </div>

                {isCustomSelected ? (
                  <div className='flex flex-col gap-2 w-full'>
                    <SydLabel label='Start date'>
                      <SydInput
                        type='date'
                        value={draft.startDate}
                        onChange={handleStartChange}
                        size='sm'
                        width='100%'
                        minWidth='0'
                        inputProps={DATE_INPUT_PROPS}
                      />
                    </SydLabel>

                    <SydLabel
                      label='End date'
                      error={showEndBeforeStartError ? "End date can't be before start date." : null}
                    >
                      <SydInput
                        type='date'
                        value={draft.endDate}
                        onChange={handleEndChange}
                        size='sm'
                        width='100%'
                        minWidth='0'
                        inputProps={DATE_INPUT_PROPS}
                      />
                    </SydLabel>
                  </div>
                ) : null}
              </div>

              <div className={clsx('flex justify-between items-center gap-2 p-2', classes.footer)}>
                <SydButton
                  variant='ghost'
                  size='sm'
                  onClick={handleResetDraft}
                  disabled={isDraftDefault}
                >
                  Reset
                </SydButton>

                <SydButton
                  variant='primary'
                  size='sm'
                  onClick={handleApply}
                  disabled={!isDirty || !rangeValid}
                >
                  Apply
                </SydButton>
              </div>
            </div>
          </ClickAwayListener>
        </div>
      </Grow>
    )
  }, [
    classes,
    closeWithoutCommit,
    draft,
    isCustomSelected,
    showEndBeforeStartError,
    isDraftDefault,
    isDirty,
    rangeValid,
    handlePresetSelect,
    handleStartChange,
    handleEndChange,
    handleResetDraft,
    handleApply
  ])

  return (
    <>
      <SelectLabel
        ref={anchorRef}
        open={open}
        onClick={handleToggleOpen}
        className='font-size-sm radius-sm'
        style={TRIGGER_STYLE}
      >
        {summaryLabel}
      </SelectLabel>

      <Popper
        transition
        open={open}
        placement='bottom-start'
        className='z-index-dropdown'
        anchorEl={anchorRef.current}
      >
        {renderMenu}
      </Popper>
    </>
  )
}
